import React from 'react';
import { motion } from 'motion/react';
import type { UploadPhase } from '../../stores/libraryStore';
import { useI18n } from '../../i18n';

interface UploadProgressOverlayProps {
  progress: number;
  phase: UploadPhase;
  stage: string;
  onDismiss: () => void;
}

const stageKeys = new Map([
  ['Uploading your document...', 'upload.stages.uploading'],
  ['Waiting for document processing...', 'upload.stages.queued'],
  ['Processing your document...', 'upload.stages.processing'],
  ['Upload complete', 'upload.stages.completed'],
]);
const phaseKeys: Partial<Record<UploadPhase, string>> = {
  uploading: 'upload.stages.uploading',
  queued: 'upload.stages.queued',
  processing: 'upload.stages.processing',
  completed: 'upload.stages.completed',
};

function AnimatedBook({ delay = 0 }: { delay?: number }) {
  return (
    <div className="relative w-28 h-16">
      <div className="absolute inset-0 rounded-md bg-gradient-to-r from-[#7367FF] to-[#2DD2FE] shadow-lg shadow-[#7367FF]/30" />
      <div className="absolute start-0 top-0 h-full w-4 rounded-s-md bg-[#5f56e6]" />
      <motion.div
        className="absolute start-4 top-[2px] h-[calc(100%-4px)] w-[calc(100%-18px)] rounded-e-md bg-white/95 shadow-inner origin-left"
        animate={{ rotateZ: [0, -14, 0, -14, 0] }}
        transition={{ duration: 1.8, repeat: Infinity, ease: 'easeInOut', delay }}
      />
    </div>
  );
}

export function UploadProgressOverlay({ progress, phase, stage, onDismiss }: UploadProgressOverlayProps) {
  const { t, isRTL, lang } = useI18n();
  const isQueued = phase === 'queued';
  const title = isQueued
    ? t('upload.queuedTitle')
    : phase === 'processing'
      ? t('upload.processingTitle')
      : t('upload.uploadingTitle');
  const fallbackSubtitle = isQueued ? t('upload.queuedSubtitle') : t('upload.preparingSubtitle');
  // Keep English server details intact; Arabic uses known copy or its current phase.
  // This projects the display only, without changing the store's stage or progress.
  const stageKey = stageKeys.get(stage) || phaseKeys[phase];
  const subtitle = lang === 'ar'
    ? (stageKey ? t(stageKey) : fallbackSubtitle)
    : stage || fallbackSubtitle;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-white/70 backdrop-blur-sm">
      <div className="rounded-3xl border border-white/40 bg-white/70 px-8 py-7 shadow-2xl">
        <div className="flex flex-col items-center">
          <div className="relative mb-5">
            <div className="relative">
              <AnimatedBook />
            </div>
            <div className="-mt-4 ms-5">
              <AnimatedBook delay={0.3} />
            </div>
          </div>
          <p className="text-sm font-medium text-[#434343]">{title}</p>
          <p className="mt-1 max-w-72 text-center text-xs text-[#434343]/60">{subtitle}</p>
          <div className="mt-4 h-2 w-56 overflow-hidden rounded-full bg-[#7367FF]/10">
            {isQueued ? (
              <motion.div
                className="h-full w-1/3 rounded-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE]"
                initial={{ x: isRTL ? '100%' : '-100%' }}
                animate={{ x: isRTL ? '-300%' : '300%' }}
                transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
              />
            ) : (
              <motion.div
                className="h-full rounded-full bg-gradient-to-r from-[#7367FF] to-[#2DD2FE]"
                initial={{ width: '0%' }}
                animate={{ width: `${Math.max(0, Math.min(100, Math.round(progress || 0)))}%` }}
                transition={{ duration: 0.3, ease: 'easeOut' }}
              />
            )}
          </div>
          <button
            type="button"
            onClick={onDismiss}
            className="mt-4 rounded-full px-4 py-2 text-xs font-medium text-[#7367FF] transition-colors hover:bg-[#7367FF]/10"
          >
            {t('upload.continueInBackground')}
          </button>
        </div>
      </div>
    </div>
  );
}
